// Persistent Audio Storage with Multi-Tier Durability & Server Auto-Healing
// Ensures custom Kabyle voice recordings NEVER disappear across server resets, container recreation, or browser restarts.
import {
  saveAudioToFirestore,
  deleteAudioFromFirestore,
  subscribeFirestoreAudios,
  fetchAllAudiosFromFirestore,
} from '../lib/firebase';

export interface AudioRecordingRecord {
  word: string;
  dataUrl: string; // Base64 audio data URL (permanent offline resilient copy)
  serverUrl?: string; // Relative URL served statically by backend (e.g. /audio/recordings/...)
  updatedAt: number;
}

const IDB_NAME = 'tamazight_audio_vault_v2';
const IDB_STORE = 'recordings';
const INDEX_STORAGE_KEY = 'tamazight_recorded_words_index_v2';
const LEGACY_STORAGE_KEY = 'kabyleGame_recordedAudios';

// In-memory cache for instant zero-latency playback in UI
const memoryAudioMap = new Map<string, string>(); // word -> dataUrl or serverUrl
const memoryDataUrlMap = new Map<string, string>(); // word -> dataUrl (pure base64)
let isInitialized = false;
let isSyncing = false;

function normalizeWordKey(word: string): string {
  return (word || '').toLowerCase().trim();
}

// ==========================================
// IndexedDB Storage Helpers (High Capacity)
// ==========================================
function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not supported'));
    }
    const request = indexedDB.open(IDB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'word' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save or update a single recording in IndexedDB
 */
async function putAudioInIDB(record: AudioRecordingRecord): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('Failed to save audio recording in IndexedDB:', err);
  }
}

/**
 * Get a single recording from IndexedDB
 */
async function getAudioFromIDB(word: string): Promise<AudioRecordingRecord | null> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(word);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * Get all recordings stored locally in IndexedDB
 */
async function getAllAudiosFromIDB(): Promise<AudioRecordingRecord[]> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

/**
 * Delete a recording from IndexedDB
 */
async function deleteAudioFromIDB(word: string): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.delete(word);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
  } catch (err) {
    console.warn('Failed to delete audio recording from IndexedDB:', err);
  }
}

// Synchronous fast word key index in localStorage for instant presence checks
function updateLocalWordIndex(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys = Array.from(memoryAudioMap.keys());
    localStorage.setItem(INDEX_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Ignore quota issues for small index
  }
}

function getLocalWordIndex(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(INDEX_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // Ignore
  }
  return [];
}

/**
 * Notify all components of audio changes
 */
function dispatchAudioUpdate(detail: { word?: string; all?: boolean; url?: string } = { all: true }): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kabyle_audio_updated', { detail }));
  }
}

/**
 * Auto-Heal: Push missing recordings from client browser to newly reset/restarted server container
 */
async function autoHealMissingRecordingsToServer(missing: Record<string, string>): Promise<number> {
  const entries = Object.entries(missing);
  const totalCount = entries.length;
  if (totalCount === 0) return 0;

  // Process in small batches of 6 items to avoid large payloads, browser timeouts & server drops
  const BATCH_SIZE = 6;
  let totalSaved = 0;

  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const chunk = Object.fromEntries(entries.slice(i, i + BATCH_SIZE));
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        break;
      }
      const res = await fetch('/api/audio-recordings/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordings: chunk }),
      }).catch(() => null);

      if (res && res.ok) {
        const result = await res.json().catch(() => null);
        if (result) {
          totalSaved += result.savedCount || Object.keys(chunk).length;

          // Update local serverUrl references
          if (result.recordings) {
            for (const [word, serverUrl] of Object.entries(result.recordings)) {
              if (typeof serverUrl === 'string') {
                const currentDataUrl = memoryDataUrlMap.get(word);
                if (currentDataUrl) {
                  await putAudioInIDB({
                    word,
                    dataUrl: currentDataUrl,
                    serverUrl,
                    updatedAt: Date.now(),
                  });
                }
              }
            }
          }
        }
      }
    } catch {
      // Server may be rebooting or temporarily offline; keep local recordings in IndexedDB safely
      break;
    }
  }

  if (totalSaved > 0) {
    console.info(`[Auto-Heal] Successfully synced ${totalSaved}/${totalCount} audio recordings with server disk.`);
  }
  return totalSaved;
}

/**
 * Fetch all audio recordings stored on the server disk, and bidirectional sync
 */
export async function syncFromSourceServer(): Promise<Record<string, string>> {
  if (typeof window === 'undefined' || isSyncing) {
    return Object.fromEntries(memoryAudioMap.entries());
  }
  isSyncing = true;

  try {
    // 1. Gather all local recordings from IndexedDB and legacy localStorage
    try {
      const localRecords = await getAllAudiosFromIDB();
      localRecords.forEach((rec) => {
        const key = normalizeWordKey(rec.word);
        if (rec.dataUrl) {
          memoryDataUrlMap.set(key, rec.dataUrl);
          if (!memoryAudioMap.has(key)) {
            memoryAudioMap.set(key, rec.serverUrl || rec.dataUrl);
          }
        }
      });
    } catch (err) {
      console.warn('Could not read from IndexedDB during sync:', err);
    }

    try {
      const rawLegacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (rawLegacy) {
        const parsed = JSON.parse(rawLegacy);
        if (parsed && typeof parsed === 'object') {
          for (const [w, dataUrl] of Object.entries(parsed)) {
            if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
              const key = normalizeWordKey(w);
              if (!memoryDataUrlMap.has(key)) {
                memoryDataUrlMap.set(key, dataUrl);
                if (!memoryAudioMap.has(key)) {
                  memoryAudioMap.set(key, dataUrl);
                }
              }
            }
          }
        }
      }
    } catch {
      // Ignore
    }

    updateLocalWordIndex();

    // 2. Fetch server manifest and recordings
    let serverRecordings: Record<string, string> = {};
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return Object.fromEntries(memoryAudioMap.entries());
      }
      // First try full endpoint to get base64 audio data if available
      const resFull = await fetch('/api/audio-recordings/full', {
        headers: { 'Cache-Control': 'no-cache' },
      }).catch(() => null);
      if (resFull && resFull.ok) {
        const dataFull = await resFull.json().catch(() => null);
        if (dataFull && dataFull.recordings) {
          for (const [key, val] of Object.entries(dataFull.recordings as Record<string, { url: string; dataUrl?: string }>)) {
            const normKey = normalizeWordKey(key);
            serverRecordings[normKey] = val.url;
            if (val.dataUrl) {
              memoryDataUrlMap.set(normKey, val.dataUrl);
              memoryAudioMap.set(normKey, val.url || val.dataUrl);
              // Save into IndexedDB so this browser can play offline
              await putAudioInIDB({
                word: normKey,
                dataUrl: val.dataUrl,
                serverUrl: val.url,
                updatedAt: Date.now(),
              });
            } else if (val.url) {
              if (!memoryAudioMap.has(normKey)) {
                memoryAudioMap.set(normKey, val.url);
              }
            }
          }
        }
      } else {
        // Fallback to basic manifest endpoint
        const res = await fetch('/api/audio-recordings', {
          headers: { 'Cache-Control': 'no-cache' },
        }).catch(() => null);
        if (res && res.ok) {
          const data = await res.json().catch(() => null);
          if (data && data.recordings) {
            serverRecordings = data.recordings;
            Object.entries(data.recordings).forEach(([key, url]) => {
              if (typeof url === 'string') {
                const normKey = normalizeWordKey(key);
                if (!memoryAudioMap.has(normKey)) {
                  memoryAudioMap.set(normKey, url);
                }
              }
            });
          }
        }
      }
    } catch {
      // Ignore network errors gracefully when offline or server restarting
    }

    // 3. AUTO-HEALING: If client has recordings that the server does NOT have (e.g. server was reset or fresh container)
    const missingOnServer: Record<string, string> = {};
    for (const [word, dataUrl] of memoryDataUrlMap.entries()) {
      if (dataUrl && (!serverRecordings[word] || !serverRecordings[word].startsWith('/audio/'))) {
        missingOnServer[word] = dataUrl;
      }
    }

    if (Object.keys(missingOnServer).length > 0) {
      await autoHealMissingRecordingsToServer(missingOnServer);
    }

    updateLocalWordIndex();
    dispatchAudioUpdate({ all: true });
  } catch (err) {
    console.error('Error during audio sync:', err);
  } finally {
    isSyncing = false;
  }

  return Object.fromEntries(memoryAudioMap.entries());
}

/**
 * Migrate legacy browser localStorage recordings to IndexedDB and server
 */
async function migrateLegacyLocalStorage(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const entries = Object.entries(parsed);
    if (entries.length === 0) return;

    for (const [word, audioDataUrl] of entries) {
      if (typeof audioDataUrl === 'string' && audioDataUrl.startsWith('data:')) {
        const key = normalizeWordKey(word);
        if (!memoryDataUrlMap.has(key)) {
          await saveRecordedAudio(word, audioDataUrl);
        }
      }
    }
  } catch (err) {
    console.error('Error migrating legacy localStorage recordings:', err);
  }
}

/**
 * Initialize and load recordings from IndexedDB and server
 */
export function loadRecordedAudios(): Record<string, string> {
  if (typeof window === 'undefined') return {};

  if (!isInitialized) {
    isInitialized = true;

    // Fast synchronous restore from local word index
    const fastIndex = getLocalWordIndex();
    fastIndex.forEach((w) => {
      if (!memoryAudioMap.has(w)) {
        memoryAudioMap.set(w, ''); // marked as present until IDB loads
      }
    });

    // Asynchronously load full records from IndexedDB and sync with server
    getAllAudiosFromIDB().then((records) => {
      records.forEach((rec) => {
        const key = normalizeWordKey(rec.word);
        if (rec.dataUrl) {
          memoryDataUrlMap.set(key, rec.dataUrl);
          memoryAudioMap.set(key, rec.serverUrl || rec.dataUrl);
        }
      });
      updateLocalWordIndex();
      dispatchAudioUpdate({ all: true });

      // Run server sync & legacy migration once
      syncFromSourceServer().then(() => {
        migrateLegacyLocalStorage();
      });

      // Start Realtime Firestore Cloud Synchronization (Instant cross-device sync)
      initFirestoreAudioSync();
    });
  }

  return Object.fromEntries(memoryAudioMap.entries());
}

let firestoreUnsub: (() => void) | null = null;
let isFirestoreSyncing = false;

/**
 * Initialize real-time bidirectional listener with Firebase Firestore
 */
export function initFirestoreAudioSync(): void {
  if (typeof window === 'undefined' || firestoreUnsub) return;

  try {
    firestoreUnsub = subscribeFirestoreAudios((allMap, changes) => {
      let changed = false;

      // Handle removed
      changes.removed.forEach((word) => {
        const key = normalizeWordKey(word);
        if (memoryAudioMap.has(key)) {
          memoryAudioMap.delete(key);
          memoryDataUrlMap.delete(key);
          deleteAudioFromIDB(key);
          changed = true;
        }
      });

      // Handle added / modified
      Object.entries(changes.added).forEach(([word, dataUrl]) => {
        const key = normalizeWordKey(word);
        if (dataUrl && memoryDataUrlMap.get(key) !== dataUrl) {
          memoryDataUrlMap.set(key, dataUrl);
          memoryAudioMap.set(key, dataUrl);
          putAudioInIDB({
            word: key,
            dataUrl,
            updatedAt: Date.now(),
          });
          changed = true;
        }
      });

      if (changed) {
        updateLocalWordIndex();
        dispatchAudioUpdate({ all: true });
      }
    });

    // Automatically push existing local recordings (like previously recorded 'aydi') to Firestore
    syncLocalAudiosToFirestore();
  } catch (err) {
    console.warn('[Firestore Audio Sync] Could not start realtime listener:', err);
  }
}

/**
 * Sync local audio vault with Cloud Firestore
 */
export async function syncLocalAudiosToFirestore(): Promise<{ pushed: number; pulled: number }> {
  if (isFirestoreSyncing) return { pushed: 0, pulled: 0 };
  isFirestoreSyncing = true;
  let pushed = 0;
  let pulled = 0;

  try {
    const cloudAudios = await fetchAllAudiosFromFirestore();
    const localRecords = await getAllAudiosFromIDB();
    const localMap: Record<string, string> = {};

    localRecords.forEach((r) => {
      if (r.dataUrl) {
        localMap[normalizeWordKey(r.word)] = r.dataUrl;
      }
    });

    // Also consider memory data URLs
    for (const [w, dataUrl] of memoryDataUrlMap.entries()) {
      if (dataUrl && !localMap[w]) {
        localMap[w] = dataUrl;
      }
    }

    // Push local ones to Firestore if missing
    for (const [word, dataUrl] of Object.entries(localMap)) {
      if (!cloudAudios[word]) {
        await saveAudioToFirestore(word, dataUrl);
        pushed++;
      }
    }

    // Pull cloud ones to local IndexedDB
    for (const [word, dataUrl] of Object.entries(cloudAudios)) {
      if (!localMap[word]) {
        memoryDataUrlMap.set(word, dataUrl);
        memoryAudioMap.set(word, dataUrl);
        await putAudioInIDB({
          word,
          dataUrl,
          updatedAt: Date.now(),
        });
        pulled++;
      }
    }

    if (pulled > 0) {
      updateLocalWordIndex();
      dispatchAudioUpdate({ all: true });
    }
  } catch (err) {
    console.warn('Error syncing with Firestore cloud:', err);
  } finally {
    isFirestoreSyncing = false;
  }

  return { pushed, pulled };
}

// Initial load on import
if (typeof window !== 'undefined') {
  loadRecordedAudios();
}

/**
 * Generate a standardized unique key for storing and retrieving audio for a specific page of a tale
 */
export function getTalePageAudioKey(scrollTitle: string, pageIndex: number): string {
  const cleanTitle = (scrollTitle || '')
    .toLowerCase()
    .replace(/[^\w\s\u00C0-\u017FɣƔɛƐčČǧǦṭṬḍḌṣṢẓẒḥḤxXqQṛṚ-]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  return `tale_${cleanTitle}_p${pageIndex + 1}`;
}

/**
 * Check if a word has a custom voice recording
 */
export function hasRecordedAudio(word: string): boolean {
  if (!word) return false;
  const key = normalizeWordKey(word);
  if (memoryAudioMap.has(key) && Boolean(memoryAudioMap.get(key))) {
    return true;
  }
  if (memoryDataUrlMap.has(key) && Boolean(memoryDataUrlMap.get(key))) {
    return true;
  }
  return false;
}

/**
 * Returns a fast Set containing all words that have custom voice recordings.
 * Instant zero-allocation retrieval directly from internal state.
 */
export function getRecordedAudiosSet(): Set<string> {
  const set = new Set<string>();
  for (const [k, v] of memoryAudioMap.entries()) {
    if (v) set.add(k);
  }
  for (const [k, v] of memoryDataUrlMap.entries()) {
    if (v) set.add(k);
  }
  return set;
}

/**
 * Get the audio URL (from source directory) or data URL for a word
 */
export function getRecordedAudio(word: string): string | null {
  if (!word) return null;
  const key = normalizeWordKey(word);
  // Prefer direct data URL for immediate instant offline playback
  if (memoryDataUrlMap.has(key) && memoryDataUrlMap.get(key)) {
    return memoryDataUrlMap.get(key) || null;
  }
  if (memoryAudioMap.has(key) && memoryAudioMap.get(key)) {
    return memoryAudioMap.get(key) || null;
  }
  return null;
}

/**
 * Save a custom voice recording with durable multi-tier persistence (IndexedDB + Server)
 */
export async function saveRecordedAudio(word: string, audioDataUrl: string): Promise<string | null> {
  if (!word || !audioDataUrl) return null;
  const key = normalizeWordKey(word);

  // 1. Instant in-memory cache for 0 latency playback in UI
  memoryDataUrlMap.set(key, audioDataUrl);
  memoryAudioMap.set(key, audioDataUrl);
  updateLocalWordIndex();
  dispatchAudioUpdate({ word: key, url: audioDataUrl });

  // 2. Persistent client storage: Save immediately to IndexedDB
  await putAudioInIDB({
    word: key,
    dataUrl: audioDataUrl,
    updatedAt: Date.now(),
  });

  // 3. Save to backend server disk (/public/audio/recordings/)
  try {
    const response = await fetch('/api/audio-recordings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ word: key, audioDataUrl }),
    });

    if (response.ok) {
      const result = await response.json();
      if (result.url) {
        // Update IndexedDB record with the server URL as well
        await putAudioInIDB({
          word: key,
          dataUrl: audioDataUrl,
          serverUrl: result.url,
          updatedAt: Date.now(),
        });
        memoryAudioMap.set(key, result.url);
        dispatchAudioUpdate({ word: key, url: result.url });
      }
    } else {
      console.warn('Server recording save returned non-OK status, kept in IndexedDB:', response.status);
    }
  } catch (err) {
    console.warn('Network error saving audio to server, saved locally in IndexedDB:', err);
  }

  // 4. Save to Firebase Firestore (Instant Cloud Synchronization across all devices)
  saveAudioToFirestore(key, audioDataUrl).catch((fireErr) => {
    console.warn('Could not sync audio recording to Firestore:', fireErr);
  });

  return audioDataUrl;
}

/**
 * Delete a custom voice recording for a word from both client and server
 */
export async function deleteRecordedAudio(word: string): Promise<void> {
  if (!word) return;
  const key = normalizeWordKey(word);

  // 1. Remove from memory
  memoryAudioMap.delete(key);
  memoryDataUrlMap.delete(key);
  updateLocalWordIndex();
  dispatchAudioUpdate({ word: key });

  // 2. Remove from IndexedDB
  await deleteAudioFromIDB(key);

  // 3. Remove from server
  try {
    await fetch(`/api/audio-recordings/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  } catch (err) {
    console.error('Error deleting audio file from server:', err);
  }

  // 4. Remove from Firebase Firestore
  deleteAudioFromFirestore(key).catch((fireErr) => {
    console.warn('Could not delete audio recording from Firestore:', fireErr);
  });
}

/**
 * Get count of all recorded words
 */
export function getRecordedWordsCount(): number {
  return memoryAudioMap.size;
}

/**
 * Export all recorded audio data URLs for backup purposes
 */
export async function getAllRecordedAudiosDataUrls(): Promise<Record<string, string>> {
  const allRecords = await getAllAudiosFromIDB();
  const map: Record<string, string> = {};

  allRecords.forEach((r) => {
    if (r.dataUrl) {
      map[normalizeWordKey(r.word)] = r.dataUrl;
    }
  });

  // Also include any in memoryDataUrlMap that might not yet be persisted
  for (const [w, dataUrl] of memoryDataUrlMap.entries()) {
    if (dataUrl && !map[w]) {
      map[w] = dataUrl;
    }
  }

  return map;
}

/**
 * Restore a batch of recorded audios (from backup import)
 */
export async function restoreRecordedAudiosBatch(recordings: Record<string, string>): Promise<number> {
  if (!recordings || typeof recordings !== 'object') return 0;
  const entries = Object.entries(recordings);
  if (entries.length === 0) return 0;

  let restoredCount = 0;
  const validBatch: Record<string, string> = {};

  for (const [word, dataUrl] of entries) {
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:audio')) {
      const key = normalizeWordKey(word);
      memoryDataUrlMap.set(key, dataUrl);
      memoryAudioMap.set(key, dataUrl);
      validBatch[key] = dataUrl;

      await putAudioInIDB({
        word: key,
        dataUrl,
        updatedAt: Date.now(),
      });
      restoredCount++;
    }
  }

  updateLocalWordIndex();
  dispatchAudioUpdate({ all: true });

  // Send batch to server to recreate files on disk
  if (Object.keys(validBatch).length > 0) {
    await autoHealMissingRecordingsToServer(validBatch);
    // Push restored batch to Firebase Firestore in background
    for (const [w, dataUrl] of Object.entries(validBatch)) {
      saveAudioToFirestore(w, dataUrl).catch(() => {});
    }
  }

  return restoredCount;
}

/**
 * Force Auto-Heal: Explicitly re-push all local IndexedDB audio recordings to the server
 */
export async function forceAutoHealRecordingsToServer(): Promise<{ success: boolean; count: number }> {
  try {
    const allDataUrls = await getAllRecordedAudiosDataUrls();
    const count = Object.keys(allDataUrls).length;
    if (count === 0) {
      return { success: true, count: 0 };
    }

    const saved = await autoHealMissingRecordingsToServer(allDataUrls);
    return { success: true, count: saved };
  } catch (err) {
    console.error('Failed to force auto-heal recordings to server:', err);
    return { success: false, count: 0 };
  }
}
